import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import axios from "axios";
import { exportPurchases, fetchPaymentsandTransactions } from "../services/PaymentsandTransactions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { getModulePermissions } from "../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";



// Payment audit (2026-08-27): the money columns tell three different
// truths. List price is the client-reported store quote; Charged is what
// the store's verification says it billed (test money never moves, so
// test rows read zero); Net (est.) is list minus the store's 15% cut.
const SOURCE_LABELS = {
    real: { text: "Real", cls: "bg-success-focus text-success-600" },
    test: { text: "License test", cls: "bg-neutral-200 text-secondary-light" },
    sandbox: { text: "Sandbox", cls: "bg-neutral-200 text-secondary-light" },
};

const money = (amount, currency) =>
    amount == null
        ? "-"
        : `${Number(amount).toFixed(2)}${currency ? " " + currency : ""}`;

// Readable product names (founder 2026-09-12). The column used to print the
// raw internal id, which is long enough to scroll clean out of a phone
// viewport, so a short id like the Read looked like an empty cell.
const PRODUCT_LABELS = {
    curiouzz_club_monthly: "Club, monthly",
    curiouzz_club_quarterly: "Club, quarterly",
    curiouzz_club_yearly: "Club, yearly",
    reflection_full: "Curiouzz Read",
    reflection_standard: "Curiouzz Read",
    reflection_premium: "Curiouzz Read",
};
const productLabel = (id) => PRODUCT_LABELS[id] || (id && id.startsWith("reignite") ? "Reignite" : id);

// Where a Charged figure came from. Billed = the store said it billed this.
// Catalogue = the store's listed price for the buyer's region, used where
// the store reports no billed figure (Google one-time products).
const CHARGED_SOURCE_TITLE = {
    store_billed: "Billed by the store",
    store_catalogue: "Store catalogue price for the buyer's region",
};

const COLUMN_DEFS = [
    { key: "user", label: "User", render: (t) => t.user?.email || "N/A" },
    // One row per charge (2026-09-25): a renewal the store made reads
    // "Club, monthly, renewal 2" (the first charge is 1) and keeps its own
    // amounts, so the date filter and the totals count every charge.
    {
        key: "product",
        label: "Product",
        render: (t) => (
            <span title={t.productId}>
                {productLabel(t.productId)}
                {t.isRenewal ? `, renewal${t.renewalNumber ? " " + t.renewalNumber : ""}` : ""}
            </span>
        ),
    },
    { key: "list", label: "List price", render: (t) => money(t.amount, t.currency) },
    {
        key: "charged",
        label: "Charged",
        render: (t) => {
            if (t.purchaseSource === "test" || t.purchaseSource === "sandbox") return "0.00";
            const text = money(t.chargedAmount, t.chargedCurrency || t.currency);
            const title = CHARGED_SOURCE_TITLE[t.chargedSource];
            return title ? <span title={title}>{text}{t.chargedSource === "store_catalogue" ? "*" : ""}</span> : text;
        },
    },
    {
        key: "net",
        label: "Net (est.)",
        render: (t) =>
            money(t.netEstimate, t.purchaseSource === "real" ? t.currency : null),
    },
    {
        key: "source",
        label: "Source",
        render: (t) => {
            const s = SOURCE_LABELS[t.purchaseSource];
            return s ? (
                <span className={`px-8 py-2 radius-4 fw-medium text-sm ${s.cls}`}>
                    {s.text}
                </span>
            ) : (
                "-"
            );
        },
    },
    { key: "platform", label: "Platform", render: (t) => t.platform },
    { key: "status", label: "Status", render: (t) => t.status },
    // Renewal (founder 2026-09-25): what the subscription behind a Club
    // purchase is doing now, from the server's utils/renewalState:
    // "Renews DD-MM-YYYY" (first period, auto-renew on), "Renewed, next ...",
    // "Cancelled, ends ..." (auto-renew off, paid through that day),
    // "Expired ...", "Refunded", "Payment failed"; "One-time" for Reignite
    // and the Read. Hover for the dates.
    {
        key: "renewal",
        label: "Renewal",
        render: (t) =>
            t.renewal?.label ? <span title={t.renewal.detail || ""}>{t.renewal.label}</span> : "-",
    },
    { key: "date", label: "Date", render: (t) => moment(t.createdAt).format("DD-MM-YYYY") },
];
const MONEY_KEYS = ["list", "charged", "net"];
const COLUMNS_STORAGE_KEY = "payments_visible_columns_v1";

const TransactionsPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    const navigate = useNavigate();
    const [exportType, setExportType] = useState("");
    const [exportLoading, setExportLoading] = useState(false);


    const [filters, setFilters] = useState({
        status: "",
        platform: "",
        userId: "",
        from: "",
        to: "",
        search: "",
        itemsPerPage: 10,
        currentPage: 1,
    });
    const [total, setTotal] = useState(0);
    const [totals, setTotals] = useState(null);
    // Adjustable columns (founder ask 2026-08-27): a per-browser preference;
    // the checkbox and Actions columns always render.
    const [visibleCols, setVisibleCols] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY));
            if (Array.isArray(saved) && saved.length) {
                // A column added after a browser saved its preference shows
                // once by default (the Renewal column, 2026-09-25); hiding
                // it afterwards sticks, because the flag is set here.
                const ADDED_FLAG = "payments_columns_added_renewal_v1";
                if (!saved.includes("renewal") && !localStorage.getItem(ADDED_FLAG)) {
                    const at = saved.indexOf("status");
                    const next = [...saved];
                    next.splice(at >= 0 ? at + 1 : next.length, 0, "renewal");
                    localStorage.setItem(ADDED_FLAG, "1");
                    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
                    return next;
                }
                return saved;
            }
        } catch { }
        return COLUMN_DEFS.map((c) => c.key);
    });
    const [colMenuOpen, setColMenuOpen] = useState(false);
    const toggleColumn = (key) => {
        setVisibleCols((prev) => {
            const next = prev.includes(key)
                ? prev.filter((k) => k !== key)
                : [...prev, key];
            if (!next.length) return prev; // a table with no columns helps nobody
            localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    };
    const cols = COLUMN_DEFS.filter((c) => visibleCols.includes(c.key));
    // Drag-to-resize is portal-wide now: see helper/GlobalColumnResizer.
    const { shouldCheckPermissions, canEdit, canAdd, canView, canDelete } =
        getModulePermissions("payments-transactions");



    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const params = {
                page: filters.currentPage,
                limit: filters.itemsPerPage,
                status: filters.status,
                platform: filters.platform,
                userId: filters.userId,
                from: filters.from,
                to: filters.to,
                search: filters.search,
            };
            const res = await fetchPaymentsandTransactions(params)
            setTransactions(res?.data?.data);
            setTotal(res?.data?.total);
            setTotals(res?.data?.totals || null);
        } catch (err) {
            console.error(err);
            setErrorMessage("Failed to fetch transactions");
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [filters]);

    // Pagination helpers
    const totalPages = Math.ceil(total / filters.itemsPerPage);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setFilters((prev) => ({ ...prev, currentPage: page }));
        }
    };

    const handleItemsPerPageChange = (e) => {
        setFilters((prev) => ({
            ...prev,
            itemsPerPage: parseInt(e.target.value, 10),
            currentPage: 1,
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
            currentPage: 1,
        }));
    };

    const getPaginationRange = () => {
        let start = Math.max(1, filters.currentPage - 2);
        let end = Math.min(totalPages, start + 4);

        if (end - start < 4) {
            start = Math.max(1, end - 4);
        }

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    const isSelected = (id) => selectedItems.includes(id);
    // Checkbox Logic
    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedItems(transactions.map((item) => item._id));
        else setSelectedItems([]);
    };

    const handleSelectItem = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };


    const handleExport = async () => {
        try {
            setExportLoading(true);

            const res = await exportPurchases({
                status: filters.status,
                platform: filters.platform,
                search: filters.search,
                from: filters.from,
                to: filters.to,
                format: exportType === "excel" ? "xlsx" : "csv",
            });

            const fileType =
                exportType === "excel"
                    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    : "text/csv";

            const fileName =
                exportType === "excel" ? "transactions.xlsx" : "transactions.csv";

            const blob = new Blob([res.data], { type: fileType });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            link.click();

        } catch (err) {
            console.error(err);
            setErrorMessage("Export failed");
        } finally {
            setExportLoading(false);
        }
    };


    return (
        <div className="card h-100 p-0 radius-12">
            {successMessage && (
                <div className="alert alert-success">{successMessage}</div>
            )}
            {errorMessage && (
                <div className="alert alert-danger">{errorMessage}</div>
            )}

            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">
                        Show
                    </span>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={filters.itemsPerPage}
                        defaultValue="Select Number"
                        onChange={handleItemsPerPageChange}
                    >
                        <option value="10">10 items per page</option>
                        <option value="20">20 items per page</option>
                        <option value="50">50 items per page</option>
                        <option value="100">100 items per page</option>
                    </select>
                    <select
                        name="status"
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Status</option>
                        <option value="succeeded">Succeeded</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                    </select>

                    <select
                        name="platform"
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={filters.platform}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Platforms</option>
                        <option value="ios">iOS</option>
                        <option value="android">Android</option>
                        <option value="web">Web</option>
                    </select>

                    <form className="navbar-search" style={{ position: "relative", maxWidth: "400px" }}>
                        <input
                            type="text"
                            name="search"
                            className="bg-base h-40-px w-auto pe-40"
                            placeholder="Search by Email"
                            value={filters.search}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    search: e.target.value,
                                    currentPage: 1,
                                }));
                            }}
                            style={{
                                paddingRight: filters.search ? "5rem" : "2.5rem",
                                borderRadius: "7px",
                            }}
                        />

                        {/* Search icon */}
                        <Icon
                            icon="ion:search-outline"
                            className="icon"
                            style={{
                                position: "absolute",
                                right: filters.search ? "3.25rem" : "1rem",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "1.25rem",
                                color: "#999",
                                pointerEvents: "none",
                            }}
                        />

                        {/* Clear button */}
                        {filters.search && (
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        search: "",
                                        currentPage: 1,
                                    }));
                                }}
                                style={{
                                    position: "absolute",
                                    right: "1rem",
                                    top: "30%",
                                    transform: "translateY(-50%)",
                                    fontSize: "1.25rem",
                                    color: "#000",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </form>


                    <div className="col-md-2" style={{ position: "relative", maxWidth: "200px" }}>
                        <DatePicker
                            selected={filters.from ? new Date(filters.from) : null}
                            onChange={(date) => setFilters({ ...filters, from: date ? date.toISOString().split("T")[0] : "" })}
                            className="form-control"
                            placeholderText="Start Date"
                            dateFormat="yyyy-MM-dd"
                        />
                        {filters.from && (
                            <button
                                type="button"
                                onClick={() => setFilters({ ...filters, from: "" })}
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "transparent",
                                    border: "none",
                                    fontSize: "1.25rem",
                                    color: "#000",
                                    cursor: "pointer",
                                    display: "flex",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </div>

                    <div className="col-md-2" style={{ position: "relative", maxWidth: "200px" }}>
                        <DatePicker
                            selected={filters.to ? new Date(filters.to) : null}
                            onChange={(date) => setFilters({ ...filters, to: date ? date.toISOString().split("T")[0] : "" })}
                            className="form-control"
                            placeholderText="End Date"
                            dateFormat="yyyy-MM-dd"
                        />
                        {filters.to && (
                            <button
                                type="button"
                                onClick={() => setFilters({ ...filters, to: "" })}
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "transparent",
                                    border: "none",
                                    fontSize: "1.25rem",
                                    color: "#000",
                                    cursor: "pointer",
                                    display: "flex",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <select
                            className="form-select form-select-sm w-auto ps-12 py-6"
                            style={{
                                fontSize: "16px",
                                borderRadius: "7px",
                                backgroundPositionX: "unset",
                                backgroundPosition: "right 0.75rem center",
                            }}
                            value={exportType}
                            onChange={(e) => setExportType(e.target.value)}
                        >
                            <option value="">Export as</option>
                            <option value="csv">CSV</option>
                            <option value="excel">Excel</option>
                        </select>

                        <button
                            className="btn btn-primary"
                            style={{ borderColor: "#FA69B9" }}
                            onClick={handleExport}
                            disabled={!exportType || exportLoading}
                        >
                            {exportLoading ? "Exporting..." : "Export"}
                        </button>

                        {errorMessage && (
                            <p className="text-danger small mb-0">{errorMessage}</p>
                        )}

                        <div style={{ position: "relative" }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary d-flex align-items-center gap-1"
                                style={{ borderRadius: "7px" }}
                                onClick={() => setColMenuOpen((o) => !o)}
                            >
                                <Icon icon="mdi:table-column" />
                                Columns
                            </button>
                            {colMenuOpen && (
                                <div
                                    className="card p-12 shadow"
                                    style={{
                                        position: "absolute",
                                        right: 0,
                                        top: "110%",
                                        zIndex: 20,
                                        minWidth: "180px",
                                    }}
                                >
                                    {COLUMN_DEFS.map((c) => (
                                        <label
                                            key={c.key}
                                            className="d-flex align-items-center gap-2 py-4 px-4"
                                            style={{ cursor: "pointer" }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={visibleCols.includes(c.key)}
                                                onChange={() => toggleColumn(c.key)}
                                            />
                                            <span>{c.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">
                                    <div className="d-flex align-items-center gap-10">
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input
                                                className="form-check-input radius-4 border input-form-dark"
                                                type="checkbox"
                                                name="checkbox"
                                                id="selectAll"
                                                onChange={handleSelectAll}
                                                checked={
                                                    selectedItems.length === transactions.length &&
                                                    transactions.length > 0
                                                }
                                            />
                                        </div>
                                    </div>
                                </th>
                                {cols.map((c) => (
                                    <th key={c.key}>{c.label}</th>
                                ))}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={cols.length + 2} className="text-center">
                                        <span className="loader-15"></span>
                                    </td>
                                </tr>
                            ) : transactions.length > 0 ? (
                                transactions.map((t) => (
                                    <tr key={t._id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-10">
                                                <div className="form-check style-check d-flex align-items-center">
                                                    <input
                                                        className="form-check-input radius-4 border border-neutral-400"
                                                        type="checkbox"
                                                        name="checkbox"
                                                        value={t._id}
                                                        checked={isSelected(t._id)}
                                                        onChange={() => handleSelectItem(t._id)}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        {cols.map((c) => (
                                            <td key={c.key}>{c.render(t)}</td>
                                        ))}
                                        <td className="d-flex gap-2">
                                            {(!shouldCheckPermissions || canView) && (
                                                <button
                                                    title="View"
                                                    onClick={() =>
                                                        navigate(`/payments-transactions/view/${t._id}`)
                                                    }
                                                    className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon icon="majesticons:eye-line" />
                                                </button>
                                            )}
                                            {/* {t.status === "succeeded" && (
                                                <button className="btn btn-sm btn-danger">
                                                    Refund
                                                </button>
                                            )} */}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={cols.length + 2} className="text-center">
                                        No Transactions Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {/* Per-currency totals over the WHOLE filtered set (not
                            just this page). Only store-confirmed real money
                            counts; test and unclassified rows surface as counts. */}
                        {totals &&
                            (totals.real?.length > 0 ||
                                totals.testCount > 0 ||
                                totals.unknownCount > 0) && (
                                <tfoot>
                                    {(totals.real || []).map((row) => {
                                        const labelKey =
                                            cols.find((c) => !MONEY_KEYS.includes(c.key))?.key || null;
                                        return (
                                            <tr key={row.currency} className="fw-semibold">
                                                <td>{labelKey ? "" : `Total (${row.currency})`}</td>
                                                {cols.map((c) => {
                                                    if (c.key === labelKey)
                                                        return (
                                                            <td key={c.key}>
                                                                {`Total, real money (${row.currency})`}
                                                            </td>
                                                        );
                                                    if (c.key === "list")
                                                        return <td key={c.key}>{row.list.toFixed(2)}</td>;
                                                    if (c.key === "charged")
                                                        return <td key={c.key}>{row.charged.toFixed(2)}</td>;
                                                    if (c.key === "net")
                                                        return <td key={c.key}>{row.net.toFixed(2)}</td>;
                                                    return <td key={c.key}></td>;
                                                })}
                                                <td></td>
                                            </tr>
                                        );
                                    })}
                                    {(totals.testCount > 0 || totals.unknownCount > 0) && (
                                        <tr>
                                            <td
                                                colSpan={cols.length + 2}
                                                className="text-secondary-light"
                                            >
                                                {totals.testCount > 0 &&
                                                    `${totals.testCount} test purchase${totals.testCount > 1 ? "s" : ""} excluded from totals (no money moved). `}
                                                {totals.unknownCount > 0 &&
                                                    `${totals.unknownCount} older row${totals.unknownCount > 1 ? "s" : ""} not yet classified.`}
                                            </td>
                                        </tr>
                                    )}
                                </tfoot>
                            )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                    <span>
                        Showing {(filters.currentPage - 1) * filters.itemsPerPage + 1} to{" "}
                        {Math.min(filters.currentPage * filters.itemsPerPage, total)} of {total} entries
                    </span>
                    <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                        {/* Prev */}
                        <li className={`page-item ${filters.currentPage === 1 ? "disabled" : ""}`}>
                            <Link
                                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#"
                                onClick={() => handlePageChange(filters.currentPage - 1)}
                            >
                                <Icon icon="ep:d-arrow-left" />
                            </Link>
                        </li>

                        {/* Page Numbers */}
                        {getPaginationRange().map((p) => (
                            <li key={p} className="page-item">
                                <Link
                                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${filters.currentPage === p ? "bg-primary-600 text-white" : "bg-neutral-200"
                                        }`}
                                    to="#"
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p}
                                </Link>
                            </li>
                        ))}

                        {/* Next */}
                        <li className={`page-item ${filters.currentPage === totalPages ? "disabled" : ""}`}>
                            <Link
                                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#"
                                onClick={() => handlePageChange(filters.currentPage + 1)}
                            >
                                <Icon icon="ep:d-arrow-right" />
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default TransactionsPage;
